/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { MyAuditContext } from '../../../../providers/MyAuditProvider';
import { Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import {
  CQReactTable,
  useCQMediaQuery
} from '@laxmimanogna/code-quick-components';
import { RebuttalIcon } from '../../../../constants/IconData';
import { format, intlFormatDistance } from 'date-fns';
import {
  defaultReactTableProp,
  paginationProps,
} from '../../../../utils/my_audits.utils';
import { toTitleCase } from '../../../../utils/common.utils';
import { renderActionComponent } from '../DashboardComponents';
import { useNavigate } from 'react-router-dom';
import {
  getAuditStatus,
  MY_AUDITS_ORDERING,
} from '../../../../constants/constants';

const columnHelper = createColumnHelper();

function Rebuttals() {
  const navigate = useNavigate();
  const auditContext = useContext(MyAuditContext);
  const [ordering, setOrdering] = useState('');
  const { getAuditRebuttals } = auditContext;

  useEffect(() => {
    let searching = !!(auditContext.filterParams.searchText && auditContext.filterParams.searchText.trim());
    auditContext.getAuditRebuttals.loadAPI("Qa", "Rebuttals Uploads", searching);
  }, [auditContext.filterParams, getAuditRebuttals.showOrdering]);

  useEffect(() => {
    if (MY_AUDITS_ORDERING.includes(ordering)) {
      getAuditRebuttals.setParams({ ordering });
    }
  }, [ordering]);
  let columns = useMemo(() => [
    columnHelper.accessor('id', {
      id: 'urgent_column',
      enableSorting: false,
      header: () => '',
      cell: info => {
        return (
          <HStack
            textAlign={'left'}
            justifyContent={'flex-end'}
            alignItems={'end'}
            // spacing={3}
          >
            <RebuttalIcon style={{ height: 20, width: 20 }} />
          </HStack>
        );
      },
    }),

    columnHelper.accessor(row => row, {
          id: 'batch',
          header: () => 'Batch',
          cell: info => {
            const allTooltip = info.getValue().batch_name || info.getValue().batch_id;    
            const tp = { label: allTooltip, placement: 'bottom', hasArrow: true, bg: 'gray.800', color: 'white', borderRadius: 'md', p: 2, openDelay: 80 };
            return (
              <Tooltip {...tp}>
                <Text 
                textAlign={'left'} 
                isTruncated 
                maxW="125px" 
                cursor="default"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                display="block"
                lineHeight="1.2" 
                >
                  {info.getValue().batch_name || info.getValue().batch_id}
                </Text>
              </Tooltip>
            );
          },
        }),

    columnHelper.accessor('upload_id', {
      header: () => 'Upload #',
      id: 'chart_id',
      cell: info => {
        return info.renderValue()
      },
    }),

    columnHelper.accessor(row => row.uploaded_date, {
      id: 'upload_date',
      header: () => 'Chart Upload',
      cell: info => {
        const cellValue = info.getValue();
        if (!cellValue) {
          return 'N/A';
        }
        return (
          <HStack justifyContent={'center'}>
            <Text ml={3}>{format(new Date(cellValue), 'MM/dd/yyyy')}</Text>
          </HStack>
        );
      },
    }),

    columnHelper.accessor('audited_date', {
      header: () => 'Audit Upload',
      id: 'audited_date',
      cell: info => {
        const auditUpload = info.getValue();
        if (!auditUpload) {
          return 'N/A';
        }
        return (
          <HStack justifyContent={'center'}>
            <Text ml={3}>{format(new Date(auditUpload), 'MM/dd/yyyy')}</Text>
          </HStack>
        );
      },
    }),

    columnHelper.accessor(p => p.specialties?.name, {
      header: () => 'Specialties',
      id: 'specialty__name',
      cell: info => {
        const specialties = info.row.original?.specialties?.name;
        if (!specialties) {
          return 'N/A';
        }
        return specialties === 'ent' ? 'ENT' : toTitleCase(specialties);
      },
    }),

    columnHelper.accessor(p => p.assigned_auditor?.name, {
      header: () => 'Auditor',
      id: 'auditor',
      cell: info => {
        const qa = info.row.original?.assigned_auditor;
        if (!qa) {
          return 'N/A';
        }
        return toTitleCase(`${qa?.first_name} ${qa?.last_name}`);
      },
    }),

    columnHelper.accessor('status', {
      header: () => 'Status',
      id: 'status',
      cell: info => {
        const status = info.row.original?.status;
        if (!status) {
          return 'N/A';
        }
        return getAuditStatus(status);
      },
    }),

    columnHelper.accessor('updated_date', {
      header: () => 'Last Updated',
      id: 'updated_date',
      cell: info => {
        let updatedAt = info.getValue();

        updatedAt = updatedAt
          ? intlFormatDistance(new Date(updatedAt), new Date(), {
              addSuffix: true,
            })
          : 'N/A';

        return (
          <HStack justifyContent={'center'}>
            <Text ml={3}>
              {' '}
              {updatedAt !== 'N/A' ? toTitleCase(updatedAt) : updatedAt}
            </Text>
          </HStack>
        );
      },
    }),

    columnHelper.accessor('actions', {
      header: () => <Text ml={-10}>Actions</Text>,
      enableSorting: false,
      cell: info => {
        return renderActionComponent(info.row.original, navigate, auditContext);
      },
    }),
  ]);
  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  function handlePreviousPage() {
    auditContext.getAuditRebuttals.loadPrevious("Qa", "Rebuttals Uploads");
  }

  function handleNextPage() {
    auditContext.getAuditRebuttals.loadNext("Qa", "Rebuttals Uploads");
  }

  function handleSizeChange(size) {
    auditContext.getAuditRebuttals.loadSize("Qa", "Rebuttals Uploads", size );
  }

  function handlePageChangeByNumber(pageNumber) {
    auditContext.getAuditRebuttals.loadPageByNumber("Qa", "Rebuttals Uploads", pageNumber);
  }

  return (
    <>
      <Box
        // pointerEvents={isUpdating ? 'none' : 'inherit'}
        overflowX={canScroll ? 'scroll' : 'inherit'}
      >
        <CQReactTable
          manualSortBy={true}
          isLoading={auditContext.isAuditUploadListLoading}
          data={auditContext.auditRebuttalsList}
          columns={columns}
          {...defaultReactTableProp}
          {...paginationProps(auditContext.getAuditRebuttals)}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onSizeChange={handleSizeChange}
          onPageChangeByNumber={handlePageChangeByNumber}
          onAsc={header => {
            setOrdering(header);
          }}
          onDesc={header => {
            setOrdering(`-${header}`);
          }}
        />
      </Box>
    </>
  );
}

export default Rebuttals;
